import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Save, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { getEhiaaInitiative, updateEhiaaInitiative } from '../../lib/api';

const emptyInitiative = {
  id: 'main',
  is_active: true,
  title_ar: '', title_en: '',
  partner_ar: '', partner_en: '',
  hero_text_ar: '', hero_text_en: '',
  intro_ar: '', intro_en: '',
  impact_title_ar: '', impact_title_en: '',
  training_title_ar: '', training_title_en: '',
  training_text_ar: '', training_text_en: '',
  employment_title_ar: '', employment_title_en: '',
  employment_text_ar: '', employment_text_en: '',
  join_title_ar: '', join_title_en: '',
  join_text_ar: '', join_text_en: '',
  closing_ar: '', closing_en: '',
  form_url: '',
  landline_phone: '', mobile_phone: '', website_url: '',
  facebook_url: '', instagram_url: '', youtube_url: '',
};

const fieldClass = 'bg-white/5 border-white/10 text-white mt-1';
const labelClass = 'text-gray-300';

const TextInput = ({ data, setData, field, label, dir = 'rtl', type = 'text', placeholder = '' }) => (
  <div>
    <Label className={labelClass}>{label}</Label>
    <Input
      type={type}
      value={data[field] || ''}
      onChange={(e) => setData({ ...data, [field]: e.target.value })}
      className={`${fieldClass} ${dir === 'ltr' ? 'phone-ltr' : ''}`}
      dir={dir}
      placeholder={placeholder}
    />
  </div>
);

const TextAreaInput = ({ data, setData, field, label, dir = 'rtl', rows = 4 }) => (
  <div>
    <Label className={labelClass}>{label}</Label>
    <Textarea
      value={data[field] || ''}
      onChange={(e) => setData({ ...data, [field]: e.target.value })}
      className={fieldClass}
      dir={dir}
      rows={rows}
    />
  </div>
);

const AdminEhiaaInitiative = () => {
  const { t } = useLanguage();
  const [data, setData] = useState(emptyInitiative);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await getEhiaaInitiative();
        setData({ ...emptyInitiative, ...res.data });
      } catch (error) {
        toast.error('Error loading initiative');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateEhiaaInitiative({ ...data, id: 'main' });
      setData({ ...emptyInitiative, ...res.data });
      toast.success(t('تم حفظ مبادرة إحياء', 'Ehiaa initiative saved'));
    } catch (error) {
      toast.error(t('حدث خطأ أثناء الحفظ', 'Error saving initiative'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-raad-rose border-t-transparent" />
      </div>
    );
  }

  return (
    <div data-testid="admin-ehiaa-initiative" className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('مبادرة إحياء', 'Ehiaa Initiative')}</h1>
          <p className="text-gray-400">{t('إدارة محتوى صفحة مبادرة إحياء وروابط التسجيل والتواصل', 'Manage the initiative page content, registration, and contact links')}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2">
            <Switch checked={data.is_active} onCheckedChange={(checked) => setData({ ...data, is_active: checked })} />
            <span className="text-gray-300 text-sm">{data.is_active ? t('مفعلة', 'Active') : t('مخفية', 'Hidden')}</span>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-gradient-raad">
            <Save className="w-4 h-4 me-2" />
            {saving ? t('جاري الحفظ...', 'Saving...') : t('حفظ', 'Save')}
          </Button>
        </div>
      </div>

      <Card className="bg-raad-card border-white/10">
        <CardHeader><CardTitle className="text-white">{t('العنوان والمقدمة', 'Title and intro')}</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput data={data} setData={setData} field="title_ar" label={t('العنوان (عربي)', 'Title (Arabic)')} dir="rtl" />
            <TextInput data={data} setData={setData} field="title_en" label={t('العنوان (إنجليزي)', 'Title (English)')} dir="ltr" />
            <TextInput data={data} setData={setData} field="partner_ar" label={t('جهة التعاون (عربي)', 'Partner (Arabic)')} dir="rtl" />
            <TextInput data={data} setData={setData} field="partner_en" label={t('جهة التعاون (إنجليزي)', 'Partner (English)')} dir="ltr" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextAreaInput data={data} setData={setData} field="hero_text_ar" label={t('نص الواجهة (عربي)', 'Hero text (Arabic)')} dir="rtl" rows={3} />
            <TextAreaInput data={data} setData={setData} field="hero_text_en" label={t('نص الواجهة (إنجليزي)', 'Hero text (English)')} dir="ltr" rows={3} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextAreaInput data={data} setData={setData} field="intro_ar" label={t('المحتوى الرئيسي (عربي)', 'Main content (Arabic)')} dir="rtl" rows={8} />
            <TextAreaInput data={data} setData={setData} field="intro_en" label={t('المحتوى الرئيسي (إنجليزي)', 'Main content (English)')} dir="ltr" rows={8} />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-raad-card border-white/10">
        <CardHeader><CardTitle className="text-white">{t('الأثر والتدريب والتوظيف', 'Impact, training, employment')}</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput data={data} setData={setData} field="impact_title_ar" label={t('عنوان الأثر (عربي)', 'Impact title (Arabic)')} dir="rtl" />
            <TextInput data={data} setData={setData} field="impact_title_en" label={t('عنوان الأثر (إنجليزي)', 'Impact title (English)')} dir="ltr" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput data={data} setData={setData} field="training_title_ar" label={t('عنوان التدريب (عربي)', 'Training title (Arabic)')} dir="rtl" />
            <TextInput data={data} setData={setData} field="training_title_en" label={t('عنوان التدريب (إنجليزي)', 'Training title (English)')} dir="ltr" />
            <TextAreaInput data={data} setData={setData} field="training_text_ar" label={t('نص التدريب (عربي)', 'Training text (Arabic)')} dir="rtl" rows={3} />
            <TextAreaInput data={data} setData={setData} field="training_text_en" label={t('نص التدريب (إنجليزي)', 'Training text (English)')} dir="ltr" rows={3} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput data={data} setData={setData} field="employment_title_ar" label={t('عنوان التوظيف (عربي)', 'Employment title (Arabic)')} dir="rtl" />
            <TextInput data={data} setData={setData} field="employment_title_en" label={t('عنوان التوظيف (إنجليزي)', 'Employment title (English)')} dir="ltr" />
            <TextAreaInput data={data} setData={setData} field="employment_text_ar" label={t('نص التوظيف (عربي)', 'Employment text (Arabic)')} dir="rtl" rows={3} />
            <TextAreaInput data={data} setData={setData} field="employment_text_en" label={t('نص التوظيف (إنجليزي)', 'Employment text (English)')} dir="ltr" rows={3} />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-raad-card border-white/10">
        <CardHeader><CardTitle className="text-white">{t('التسجيل والتواصل', 'Registration and contact')}</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput data={data} setData={setData} field="join_title_ar" label={t('عنوان الانضمام (عربي)', 'Join title (Arabic)')} dir="rtl" />
            <TextInput data={data} setData={setData} field="join_title_en" label={t('عنوان الانضمام (إنجليزي)', 'Join title (English)')} dir="ltr" />
            <TextAreaInput data={data} setData={setData} field="join_text_ar" label={t('نص الانضمام (عربي)', 'Join text (Arabic)')} dir="rtl" rows={6} />
            <TextAreaInput data={data} setData={setData} field="join_text_en" label={t('نص الانضمام (إنجليزي)', 'Join text (English)')} dir="ltr" rows={6} />
            <TextInput data={data} setData={setData} field="closing_ar" label={t('الخاتمة (عربي)', 'Closing (Arabic)')} dir="rtl" />
            <TextInput data={data} setData={setData} field="closing_en" label={t('الخاتمة (إنجليزي)', 'Closing (English)')} dir="ltr" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput data={data} setData={setData} field="form_url" label={t('رابط نموذج التسجيل', 'Registration form URL')} dir="ltr" />
            <TextInput data={data} setData={setData} field="website_url" label={t('الموقع الإلكتروني', 'Website')} dir="ltr" />
            <TextInput data={data} setData={setData} field="landline_phone" label={t('الهاتف الأرضي', 'Landline')} dir="ltr" />
            <TextInput data={data} setData={setData} field="mobile_phone" label={t('الموبايل', 'Mobile')} dir="ltr" />
            <TextInput data={data} setData={setData} field="facebook_url" label="Facebook" dir="ltr" />
            <TextInput data={data} setData={setData} field="instagram_url" label="Instagram" dir="ltr" />
            <TextInput data={data} setData={setData} field="youtube_url" label="YouTube" dir="ltr" />
          </div>

          {data.form_url && (
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <a href={data.form_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 me-2" />
                {t('فتح نموذج التسجيل', 'Open registration form')}
              </a>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEhiaaInitiative;
